import { 
  ParameterGroup, 
  ParameterDefinition, 
  ParameterValue,
  SliderParameter,
  ColorParameter,
  ToggleParameter,
  SelectParameter,
  TextParameter,
  NumberParameter,
  SliderConfig,
  ColorConfig,
  ToggleConfig,
  SelectConfig,
  TextConfig,
  NumberConfig
} from '../types/index.js';

export class ParameterSystem {
  // Parameter system for Phase 2 (new object-based approach)
  private parameterDefinitions = new Map<string, ParameterDefinition>();
  private parameterGroups = new Map<string, ParameterGroup>();
  private parameterValues = new Map<string, ParameterValue>();
  private parameterObjects = new Map<string, any>(); // Maps parameter names to their objects
  private parametersDefined = false;
  private initialValuesSynced = false; // Track if initial values have been synced from host
  
  // Debug logging control
  private debugMode = false;

  /**
   * Enable or disable debug logging
   */
  setDebugMode(enabled: boolean): void {
    this.debugMode = enabled;
  }

  /**
   * Debug logging helper
   */
  private debugLog(message: string, ...args: any[]): void {
    if (this.debugMode) {
      console.log(message, ...args);
    }
  }

  // Message posting callback
  private postMessageCallback: (type: string, data?: any) => void;

  constructor(postMessageCallback: (type: string, data?: any) => void) {
    this.postMessageCallback = postMessageCallback;
  }

  // Parameter helper function implementations (return parameter objects)
  public createSliderParameter(defaultValue: number, config: SliderConfig): SliderParameter {
    const paramName = config.label;
    const sliderObject = {
      value: defaultValue,
      min: config.min ?? 0,
      max: config.max ?? 100,
      step: config.step ?? 1,
      label: config.label,
      description: config.description ?? '',
      group: config.group ?? 'general',
      category: config.category ?? 'general',
    };

    const definition: ParameterDefinition = {
      type: 'slider',
      defaultValue,
      label: sliderObject.label,
      description: sliderObject.description,
      group: sliderObject.group,
      category: sliderObject.category,
      config: {
        min: sliderObject.min,
        max: sliderObject.max,
        step: sliderObject.step
      }
    };

    this.storeParameterDefinition(paramName, definition);
    this.parameterObjects.set(paramName, sliderObject);
    return sliderObject;
  }

  public createColorParameter(defaultValue: string, config: ColorConfig): ColorParameter {
    const paramName = config.label;
    const colorObject = {
      value: defaultValue,
      label: config.label,
      description: config.description ?? '',
      group: config.group ?? 'general',
      category: config.category ?? 'general',
    };

    const definition: ParameterDefinition = {
      type: 'color',
      defaultValue,
      label: colorObject.label,
      description: colorObject.description,
      group: colorObject.group,
      category: colorObject.category,
    };

    this.storeParameterDefinition(paramName, definition);
    this.parameterObjects.set(paramName, colorObject);
    return colorObject;
  }

  public createToggleParameter(defaultValue: boolean, config: ToggleConfig): ToggleParameter {
    const paramName = config.label;
    const toggleObject = {
      value: defaultValue,
      label: config.label,
      description: config.description ?? '',
      group: config.group ?? 'general',
      category: config.category ?? 'general'
    };

    const definition: ParameterDefinition = {
      type: 'toggle',
      defaultValue,
      label: toggleObject.label,
      description: toggleObject.description,
      group: toggleObject.group,
      category: toggleObject.category
    };

    this.storeParameterDefinition(paramName, definition);
    this.parameterObjects.set(paramName, toggleObject);
    return toggleObject;
  }

  public createSelectParameter(defaultValue: string | number, config: SelectConfig): SelectParameter {
    const paramName = config.label;
    const selectObject = {
      value: defaultValue,
      options: config.options,
      label: config.label,
      description: config.description ?? '',
      group: config.group ?? 'general',
      category: config.category ?? 'general'
    };

    const definition: ParameterDefinition = {
      type: 'select',
      defaultValue,
      label: selectObject.label,
      description: selectObject.description,
      group: selectObject.group,
      category: selectObject.category,
      config: {
        options: selectObject.options
      }
    };

    this.storeParameterDefinition(paramName, definition);
    this.parameterObjects.set(paramName, selectObject);
    return selectObject;
  }

  public createTextParameter(defaultValue: string, config: TextConfig): TextParameter {
    const paramName = config.label;
    const textObject = {
      value: defaultValue,
      maxLength: config.maxLength ?? 1000,
      label: config.label,
      description: config.description ?? '',
      group: config.group ?? 'general',
      category: config.category ?? 'general'
    };

    const definition: ParameterDefinition = {
      type: 'text',
      defaultValue,
      label: textObject.label,
      description: textObject.description,
      group: textObject.group,
      category: textObject.category,
      config: {
        maxLength: textObject.maxLength
      }
    };

    this.storeParameterDefinition(paramName, definition);
    this.parameterObjects.set(paramName, textObject);
    return textObject;
  }

  public createNumberParameter(defaultValue: number, config: NumberConfig): NumberParameter {
    const paramName = config.label;
    const numberObject = {
      value: defaultValue,
      min: config.min ?? 0,
      max: config.max ?? 100,
      step: config.step ?? 1,
      label: config.label,
      description: config.description ?? '',
      group: config.group ?? 'general',
      category: config.category ?? 'general'
    };

    const definition: ParameterDefinition = {
      type: 'number',
      defaultValue,
      label: numberObject.label,
      description: numberObject.description,
      group: numberObject.group,
      category: numberObject.category,
      config: {
        min: numberObject.min,
        max: numberObject.max,
        step: numberObject.step
      }
    };

    this.storeParameterDefinition(paramName, definition);
    this.parameterObjects.set(paramName, numberObject);
    return numberObject;
  }

  private storeParameterDefinition(name: string, definition: ParameterDefinition): void {
    // This is called during scene initialization, not during define()
    // We'll collect these and send them when the scene is fully loaded
    this.parameterDefinitions.set(name, definition);
    this.parameterValues.set(name, definition.defaultValue);
  }

  public updateParameterValue(name: string, value: ParameterValue): boolean {
    const definition = this.parameterDefinitions.get(name);
    if (!definition) {
      console.warn(`Unknown parameter: ${name}. Available parameters:`, Array.from(this.parameterDefinitions.keys()));
      return false;
    }

    // Validation
    if (!this.validateParameterValue(name, value, definition)) {
      console.warn(`Validation failed for parameter ${name} = ${value}`);
      return false;
    }

    // Check if value actually changed (but always allow initial sync)
    const currentValue = this.parameterValues.get(name);
    const isInitialSync = !this.initialValuesSynced; // During initial parameter sync from host
    
    if (currentValue === value && !isInitialSync) {
      return false; // No change (but allow initial sync even if values match)
    }

    // Update value in both storage and parameter object
    this.parameterValues.set(name, value);
    
    // Update the parameter object's value property
    const parameterObject = this.parameterObjects.get(name);
    if (parameterObject) {
      parameterObject.value = value;
    }
    
    return true;
  }

  private validateParameterValue(name: string, value: ParameterValue, definition: ParameterDefinition): boolean {
    // Custom validation function
    if (definition.validate && !definition.validate(value)) {
      console.error(`Custom validation failed for parameter '${name}': ${value}`);
      return false;
    }

    // Type validation
    switch (definition.type) {
      case 'slider':
      case 'number':
        if (typeof value !== 'number' || isNaN(value)) {
          console.error(`Parameter '${name}' must be a number, got: ${value}`);
          return false;
        }
        if (definition.config?.min !== undefined && value < definition.config.min) {
          console.error(`Parameter '${name}' value ${value} is below minimum ${definition.config.min}`);
          return false;
        }
        if (definition.config?.max !== undefined && value > definition.config.max) {
          console.error(`Parameter '${name}' value ${value} is above maximum ${definition.config.max}`);
          return false;
        }
        break;
      case 'color':
        if (typeof value !== 'string' || !/^#[0-9A-Fa-f]{6}$/.test(value)) {
          console.error(`Parameter '${name}' must be a valid hex color, got: ${value}`);
          return false;
        }
        break;
      case 'toggle':
        if (typeof value !== 'boolean') {
          console.error(`Parameter '${name}' must be a boolean, got: ${value}`);
          return false;
        }
        break;
      case 'select':
        if (!definition.config?.options || !definition.config.options.includes(value as string & number)) {
          console.error(`Parameter '${name}' value ${value} is not in options: ${definition.config?.options}`);
          return false;
        }
        break;
      case 'text':
        if (typeof value !== 'string') {
          console.error(`Parameter '${name}' must be a string, got: ${value}`);
          return false;
        }
        if (definition.config?.maxLength && value.length > definition.config.maxLength) {
          console.error(`Parameter '${name}' text too long: ${value.length} > ${definition.config.maxLength}`);
          return false;
        }
        break;
    }

    return true;
  }

  // Reset parameter state (called when loading new scene)
  public resetParameterState(): void {
    this.parametersDefined = false;
    this.initialValuesSynced = false;
    this.parameterDefinitions.clear();
    this.parameterGroups.clear();
    this.parameterValues.clear();
    this.parameterObjects.clear();
  }

  // Send all parameters (from helper functions) to host
  public sendAllParametersToHost(): void {
    // Don't send if already sent or if no parameters defined
    if (this.parametersDefined || this.parameterDefinitions.size === 0) {
      return;
    }

    try {
      // Group parameters by their group property
      const groups = new Map<string, ParameterGroup>();

      for (const [paramName, paramDef] of this.parameterDefinitions) {
        const groupName = paramDef.group || 'general';
        
        if (!groups.has(groupName)) {
          // Use the parameter's category if available, otherwise default to 'general'
          const category = paramDef.category || 'general';
          groups.set(groupName, {
            groupName,
            category,
            parameters: {}
          });
        }

        const group = groups.get(groupName)!;
        group.parameters[paramName] = paramDef;
      }

      this.parametersDefined = true;

      // Send parameter definitions to host for UI generation
      this.postMessageCallback('parameters-defined', {
        groups: Array.from(groups.values()),
        timestamp: performance.now()
      });

      this.debugLog(`All parameters sent to host: ${this.parameterDefinitions.size} parameters in ${groups.size} groups`);
    } catch (error) {
      this.postMessageCallback('parameter-validation-error', {
        message: `Failed to send parameters to host: ${(error as Error).message}`,
        code: 'PARAMETER_SENDING_ERROR'
      });
    }
  }

  // Mark initial values as synced
  public markInitialValuesSynced(): void {
    this.initialValuesSynced = true;
  }

  // Get parameter count for performance reporting
  public getParameterCount(): number {
    return this.parameterDefinitions.size;
  }
} 